import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { EditorContent, ReactNodeViewRenderer, ReactRenderer, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { lowlight } from '../lib/highlight';
import { CodeBlockView } from './CodeBlockView';
import type { MentionUser } from '../lib/access';
import { Icon } from './Icon';

/**
 * WYSIWYG rich-text editor (TipTap) for access-request notes/comments — the
 * Slack/Discord experience: type in normal mode and see formatting live (no
 * markdown syntax shown), select text for a floating format toolbar, and @-type
 * to mention staff. StarterKit's input-rules still let power users type `**x**`.
 * Stores MARKDOWN (via @tiptap/markdown) so the backend/audit/timeline are
 * unchanged; the timeline renders that markdown with <Markdown>.
 */

// @tiptap/markdown adds editor.getMarkdown() (on the editor) + editor.markdown (a
// manager with parse/serialize). Type the bits we use.
type MdEditor = Editor & { getMarkdown: () => string; markdown: { parse: (md: string) => object } };
const getMarkdown = (e: Editor) => (e as MdEditor).getMarkdown();

// Serialize a mention node to `@<email>` markdown — @tiptap/markdown reads
// `renderMarkdown(node, helpers)` off the extension config (else it dumps the
// node's raw attrs into the stored note).
const MentionMd = Mention.extend({
  renderMarkdown(node: { attrs: { id: string } }) {
    return `@${node.attrs.id}`;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any);

export interface RichEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  disabled?: boolean;
  mentionUsers?: MentionUser[];
}

export function RichEditor({ value, onChange, placeholder, disabled, mentionUsers = [] }: RichEditorProps) {
  // Suggestion config is created once (editor is created once) but must read the
  // latest fetched users — keep them in a ref the items() callback reads.
  const usersRef = useRef<MentionUser[]>(mentionUsers);
  usersRef.current = mentionUsers;

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      // codeBlock: false → replaced by CodeBlockLowlight (syntax highlighting +
      // the per-block language picker NodeView).
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false }),
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockView);
        },
      }).configure({ lowlight }),
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      MentionMd.configure({
        HTMLAttributes: { class: 'mention-chip' },
        renderText: ({ node }) => `@${node.attrs.id}`,
        suggestion: {
          items: ({ query }) => {
            const q = query.toLowerCase();
            return usersRef.current
              .filter((u) => u.email.toLowerCase().includes(q) || (u.displayName ?? '').toLowerCase().includes(q))
              .slice(0, 50);
          },
          render: makeMentionRenderer,
        },
      }),
    ],
    content: value,
    // parse `content` above as markdown (option consumed by @tiptap/markdown)
    ...( { contentType: 'markdown' } as object),
    onUpdate: ({ editor }) => onChange(getMarkdown(editor)),
  });

  // External value changes (e.g. parent resets to '' after submit) → reset once.
  useEffect(() => {
    if (!editor) return;
    if (value !== getMarkdown(editor)) {
      editor.commands.setContent((editor as MdEditor).markdown.parse(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return <div className="rich-editor" />;

  // ReactNode, not string: the toolbar mixes text marks (B, i, S) with Icon
  // elements now that the emoji buttons are gone.
  const btn = (label: ReactNode, title: string, active: boolean, fn: () => void) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={active ? 'active' : ''}
      onMouseDown={(e) => e.preventDefault()}
      onClick={fn}
    >
      {label}
    </button>
  );

  const c = () => editor.chain().focus();

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        {btn('B', 'Bold (⌘B)', editor.isActive('bold'), () => c().toggleBold().run())}
        {btn('i', 'Italic (⌘I)', editor.isActive('italic'), () => c().toggleItalic().run())}
        {btn('S̶', 'Strikethrough', editor.isActive('strike'), () => c().toggleStrike().run())}
        {btn('</>', 'Inline code', editor.isActive('code'), () => c().toggleCode().run())}
        {btn('{ }', 'Code block (⌘⌥C)', editor.isActive('codeBlock'), () => c().toggleCodeBlock().run())}
        {btn('• ', 'Bulleted list', editor.isActive('bulletList'), () => c().toggleBulletList().run())}
        {btn('1.', 'Numbered list', editor.isActive('orderedList'), () => c().toggleOrderedList().run())}
        {btn(<Icon name="quote" />, 'Quote', editor.isActive('blockquote'), () => c().toggleBlockquote().run())}
        {btn(<Icon name="external" />, 'Link', editor.isActive('link'), () => {
          const url = window.prompt('Link URL');
          if (url) c().setLink({ href: url }).run();
        })}
        {btn('@', 'Mention someone', false, () => c().insertContent('@').run())}
      </div>

      <EditorContent editor={editor} className="rich-content" />
    </div>
  );
}

/* ── @mention suggestion popup — a positioned <body> div; selection + keyboard
   are managed in this closure (NOT via a React ref, which proved unreliable),
   so ↑/↓/Enter/Tab work; the React component is display-only. ── */

function MentionList({
  items,
  selected,
  onPick,
}: {
  items: MentionUser[];
  selected: number;
  onPick: (i: number) => void;
}) {
  if (!items.length) return <div className="mention-popup empty">No matches</div>;
  return (
    <div className="mention-popup">
      {items.map((u, i) => (
        <button
          type="button"
          key={u.email}
          className={i === selected ? 'active' : ''}
          onMouseDown={(e) => { e.preventDefault(); onPick(i); }}
        >
          <span className="mention-name">{u.displayName || u.email}</span>
          {u.displayName && <span className="mention-email">{u.email}</span>}
        </button>
      ))}
    </div>
  );
}

function makeMentionRenderer() {
  let component: ReactRenderer | null = null;
  let el: HTMLDivElement | null = null;
  let items: MentionUser[] = [];
  let selected = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let command: (a: { id: string; label: string }) => void = () => {};

  const place = (rect: (() => DOMRect | null) | null | undefined) => {
    if (!el || !rect) return;
    const r = rect();
    if (!r) return;
    el.style.top = `${r.bottom + window.scrollY + 4}px`;
    el.style.left = `${r.left + window.scrollX}px`;
  };
  const pick = (i: number) => {
    const u = items[i];
    if (u) command({ id: u.email, label: u.displayName || u.email });
  };
  const render = () => component?.updateProps({ items, selected, onPick: pick });

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onStart: (props: any) => {
      items = props.items; selected = 0; command = props.command;
      component = new ReactRenderer(MentionList, { props: { items, selected, onPick: pick }, editor: props.editor });
      el = document.createElement('div');
      el.className = 'mention-popup-anchor';
      document.body.appendChild(el);
      el.appendChild(component.element as HTMLElement);
      place(props.clientRect);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onUpdate: (props: any) => { items = props.items; selected = 0; command = props.command; render(); place(props.clientRect); },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onKeyDown: (props: any) => {
      const k = props.event.key;
      if (!items.length) return k === 'Escape';
      if (k === 'ArrowDown') { selected = (selected + 1) % items.length; render(); return true; }
      if (k === 'ArrowUp') { selected = (selected + items.length - 1) % items.length; render(); return true; }
      if (k === 'Enter' || k === 'Tab') { pick(selected); return true; }
      if (k === 'Escape') return true;
      return false;
    },
    onExit: () => { el?.remove(); component?.destroy(); el = null; component = null; },
  };
}
